import { INTERNET_DELAY } from "@/constants";
import * as Sentry from '@sentry/react';
import { useMount, useToggle } from "ahooks";
import React, { createContext, useCallback, useContext } from 'react';
import { HashRouter as BrowserRouter,useNavigate } from "react-router-dom";
import { getBankInfo, getPaymentType } from "./apis/user";
import { PopupProvider } from "./components/Popup";
import { USER_TOKEN } from "./constants";
import { LogInit } from "./libs/logger";
import { fetchCDN, init } from "./logic/init";
import { useNotification } from "./logic/notification";
import * as Splash from "./pages/splash";
import Router from "./routes";
import {
  selectorLocalStorage,
  useConfigStore,
  useLocalStorage,
} from "./stores";
import { useBankStore } from "./stores/banks";
import { usePaymentsStore } from "./stores/payments";
import { useUserInfoStore } from "./stores/userInfo";
import { GetConfig } from "./types/Api/Common/Home/getConfig";
import { GetBankInfo } from "./types/Api/User/PaymentType/getBankInfo";
import {
  setBusinessAxiosBaseURL,
  setBusinessAxiosToken,
} from "./utils/request";
import { checkUpdate } from "./libs/checkUpdate";
import { decodeScheme } from "./libs/app";
import { UserInfo } from "node:os";

interface ConfigContextInterface {
  config: GetConfig["response"] | null;
  setConfig: (config: GetConfig["response"]) => void;
  setCDN: (cdn: string) => void;
  getSourcePath: (path?: string) => string;
  banks: GetBankInfo["response"]["list"];
  updateBanks: () => void;
  updateUserPayments: () => void;
  isH5Plus: boolean;
}

const ConfigContext = createContext<ConfigContextInterface | null>(null);
export const ConfigProvider = ({ children }: { children: any }) => {
  const [, createNotificationPolling] = useNotification();
  const [isInitialized, { toggle: toggleIsInitialized }] = useToggle(false);
  const [userInfo,updateUserInfo] = useUserInfoStore()((state) => [
    state.userInfo,
    state.updateUserInfo,
  ]);
  const [updatePayments] = usePaymentsStore((state) => [state.updatePayments]);
  const [config, setConfig, setCDN, cdn,isH5Plus] = useConfigStore((state) => [
    state.config,
    state.setConfig,
    state.setCDN,
    state.cdn,
    state.isH5Plus,
  ]);
  let [token, setToken] =
    useLocalStorage<string>(USER_TOKEN)(selectorLocalStorage);
  const [banks, setBanks] = useBankStore((state) => [
    state.banks,
    state.setBanks,
  ]);
  const [, setPayments] = usePaymentsStore((state) => [
    state.formatPayments,
    state.setPayments,
  ]);

    const navigate = useNavigate();

  const getSourcePath = (path = "") => {
    if (path.startsWith("/images/avatar")) return `.${path}`;
    if (cdn) {
      const isClosedCDNEnds = cdn.endsWith("/");
      const isClosedPathStarts = path.startsWith("/");
      const _cdn = isClosedCDNEnds ? cdn : `${cdn}/`;
      const _path = isClosedPathStarts ? path.replace(/^\//, "") : path;
      return _cdn + _path;
    } else {
      return "";
    }
  };

  /* updateBanks 函数负责从服务器获取银行列表并使用新列表更新状态。它对“getBankInfo”端点进行 API
  调用，如果响应成功，它将使用从服务器返回的银行列表来设置银行状态。如果响应不成功，则会记录一条错误消息。 */
  const updateBanks = useCallback(async () => {
    const bankResponse = await getBankInfo({});
    if (bankResponse.ok) {
      setBanks(bankResponse.data?.list || []);
    } else {
      // TODO: 上报获取银行卡失败
      Sentry.captureException(new Error('获取银行卡列表失败'), {
        tags: {
          capture_fn: "上报获取银行卡失败",
        },
      });
      console.error(...LogInit.error("获取银行卡列表失败"));
    }
  }, [setBanks]);

  const updateUserPayments = useCallback(async () => {
    const paymentsResponse = await getPaymentType({});
    if (paymentsResponse.ok) {
      setPayments(paymentsResponse.data?.list || []);
    } else {
      // TODO: 上报获取当前用户的支付方式失败、
      Sentry.captureException(new Error('上报获取当前用户的支付方式失败'));
    }
  }, [setPayments]);

  useMount(() => {
    //初始化刷时间
    window.localStorage.setItem(INTERNET_DELAY, "100000");
    // 从自家平台跳转的域名会附带 ?t=token 需要先填充
    const domainSearch = new URLSearchParams(window.location.search);
    if (domainSearch.has("t")) {
      token = domainSearch.get("t") || token;
      setToken(token);
    }
    Sentry.captureMessage(`start init`,"info");
    init()
      .then(async ({ data, cdns, api }) => {
        // 设置业务请求的服务器地址
        Sentry.captureMessage(`设置业务请求的服务器地址： ${JSON.stringify(api)}`,"info");
        setBusinessAxiosBaseURL(api);
        // 获取CDN地址
        Sentry.captureMessage(`开始获取CDN`,"info");
        fetchCDN(cdns, data.image)
          .then((url) => {
            Sentry.captureMessage(`设置CDN ${url}`,"info");
            setCDN(url)
          })
          .catch((error) => {
            Sentry.captureException(error, {tags:{
              "capture_fn": "fetchCDN"
            }})
            console.error(...LogInit.error(error));
          });
        Sentry.captureMessage(`开始写配置： ${JSON.stringify(data)}`,"info");
        // 写入配置
        setConfig(data);

        // 判断更新，强制更新
        // @ts-ignore
        checkUpdate(window._appVer, data.appVersion);

        // 设置token
        token && setBusinessAxiosToken(token);
        // 更新用户信息
        token && (await updateUserInfo());
        // 更新银行卡列表
        token && (await updateBanks());
        // 更新当前用户的支付方式
        token && (await updatePayments());
        // 开始轮询通知
        token && createNotificationPolling();
        // 完成初始化
        Sentry.captureMessage(`初始化完成`,"info");
        toggleIsInitialized();

        const h5plus = window.plus as any;
      if (h5plus) {
          // 处理唤起传参
          // let user = userInfo as any;
          // if( !token ){
          //   user =  await updateUserInfo();
          // }
           // @ts-ignore
           window.h5p && window.h5p.on("arguments", (args) => {
            // alert(`唤起传参:${args} ==token==${token}==userInfo==${userInfo}}`)
            console.log(`唤起传参:${args} vs:${domainSearch.get("scheme")}`);
            decodeScheme(args,token||userInfo||"",navigate);
          });
        }

      })
      .catch((err) => {
        // TODO: 初始化业务逻辑失败
        Sentry.captureException(err, {
          tags: {
            capture_fn: "init",
          },
        });
        console.error(err);
      });
  });

  if (!isInitialized) return <Splash.Splash1></Splash.Splash1>;

  return (
    <ConfigContext.Provider
      value={{
        config,
        setConfig,
        setCDN,
        getSourcePath,
        banks,
        updateBanks,
        updateUserPayments,
        isH5Plus,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfigContext = () => {
  return useContext(ConfigContext) as ConfigContextInterface;
};

const BaseApp = () => {
  // https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
  // see more about Sentry.ErrorBoundary and how to use fallback function
  return (
    <Sentry.ErrorBoundary fallback={({ error, componentStack, resetError }) => (
      <React.Fragment>
        <div>Oops 产生了一个错误，请截图并联系客服反馈错误。</div>
        <div>{(error as Error).toString()}</div>
        <div>{componentStack}</div>
        <button
          onClick={() => {
            {
              /* When resetError() is called it will remove the Fallback component */
            }
            {
              /* and render the Sentry ErrorBoundary's children in their initial state */
            }
            resetError();
          }}
        >
          点击重置
        </button>
      </React.Fragment>
    )}>
      <BrowserRouter>
        <ConfigProvider>
          <PopupProvider>
            <Router />
          </PopupProvider>
        </ConfigProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
};

export const App = Sentry.withProfiler(BaseApp)
